import { createStyles, makeStyles } from "@material-ui/core";
import classnames from "classnames";
import { format } from "date-fns";
import * as React from "react";
import styled from "styled-components";

import { camelize, currencyFormatter, Label, Link, Text } from "../";
import { textWithLinks } from "../../modules";
import { RelodeTheme } from "../styles";

const AdornmentContainer = styled.div`
    display: flex;
    justify-content: start;
`;

const TextWithLinks = styled.div`
    white-space: pre-line;
    word-break: break-word;
`;

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            startAdornment: {
                marginLeft: 8,
            },
            endAdornment: {
                marginRight: 8,
            },
        }),
    { name: "DisplayField" }
);

const formatDate = (date: string | Date, notAvailableText?: string) => {
    if (!date) return notAvailableText;

    let _date: Date;

    if (typeof date === "string") {
        _date = new Date(date + "T12:00:00");
    } else {
        _date = date;
    }

    try {
        return format(_date, "MM/dd/yyyy");
    } catch {
        console.error(
            `invalid date format.  Date should be yyyy-MM-dd.  Date given: ${date}`
        );
        return "unknown date format";
    }
};

const formatTimestamp = (timestamp: number, showTime: boolean) =>
    format(timestamp, `P${showTime ? " p" : ""}`);

type DisplayMode =
    | "text"
    | "textWithLinks"
    | "date"
    | "money"
    | "percentage"
    | "url"
    | "timestamp";

type CommonProps = {
    label: string;
    component?: React.ReactNode;
    notAvailableText?: string;
    mode?: DisplayMode;
    fullWidth?: boolean;
    hideWhenEmpty?: boolean;
    endElement?: React.ReactNode;
    startElement?: React.ReactNode;
    customElement?: React.ReactNode;
    inline?: boolean;
    hidden?: boolean;
    forInput?: boolean;
    showHeader?: boolean;
    headerText?: string;
};

// provides type safety on value/showTime props depending value of mode
// still have to check in code at runtime
type ModeProps =
    | {
          mode?: "text";
          value?: string | number;
          showTime?: never;
          preserveTextFormat?: boolean;
      }
    | {
          mode?: "textWithLinks";
          value?: string;
          showTime?: never;
          preserveTextFormat?: boolean;
      }
    | {
          mode: "date";
          value?: string | Date;
          showTime?: never;
          preserveTextFormat?: never;
      }
    | {
          mode: "money";
          value?: number;
          showTime?: never;
          preserveTextFormat?: never;
      }
    | {
          mode: "percentage";
          value?: number | string;
          showTime?: never;
          preserveTextFormat?: never;
      }
    | {
          mode: "url";
          value?: string;
          showTime?: never;
          preserveTextFormat?: never;
      }
    | {
          mode: "timestamp";
          value?: number;
          showTime?: boolean;
          preserveTextFormat?: never;
      };

type DisplayFieldProps = CommonProps & ModeProps;

const WrappedUrl = ({ url }: { url: string }) => (
    <Link href={url} target="_blank" highlightType="external" rel="noopener">
        {url}
    </Link>
);

/**
 * used to display a piece of data in one of several forms.
 * @param mode Text, date, money, timestamp, url
 * @param label Text to appear as the label
 * @param notAvailableText Text to appear when the given value is undefined/null.  Defaults to "n/a"
 * @param fullWidth Component will take full width of parent
 * @param hideWhenEmpty Hides the entire component including label when value is undefined or null
 * @param endElement Component placed after the value
 * @param startElement Component placed before the value
 * @param inline Shows the value inline with label
 * @param hidden if true the entire component will not be rendered
 * @param showHeader will show header label directly above rendered component
 * @param headerText text to appear as the header label
 */
export const DisplayField = (props: DisplayFieldProps) => {
    const {
        label,
        value,
        component,
        mode = "text",
        notAvailableText,
        fullWidth,
        showTime,
        preserveTextFormat,
        hideWhenEmpty,
        endElement,
        inline,
        startElement,
        customElement,
        hidden,
        showHeader,
        headerText,
        ...passthrough
    } = props;

    const classes = useStyles({});
    const [displayValue, setDisplayValue] = React.useState<
        string | React.ReactNode
    >();

    const dataCy = passthrough["data-cy"] ?? camelize(label);
    const labelCy = !!dataCy ? `${dataCy}-label` : undefined;
    const textCy = !!dataCy ? `${dataCy}-text` : undefined;

    React.useEffect(() => {
        if (hidden) return;
        const textClasses = classnames({
            [classes.startAdornment]: !!startElement,
            [classes.endAdornment]: !!endElement,
        });

        const naText =
            notAvailableText !== undefined && notAvailableText !== null
                ? notAvailableText
                : "n/a";

        if (
            value !== null &&
            value !== undefined &&
            component !== null &&
            component !== undefined
        ) {
            throw new Error(
                "DisplayData component cannot have both a value prop and a component prop."
            );
        }

        if (component !== null && component !== undefined) {
            setDisplayValue(component);
            return;
        }

        if (value === undefined || value === null) {
            setDisplayValue(
                <Text variant="body1" dataCy={textCy}>
                    {naText}
                </Text>
            );
            return;
        }

        let valueToDisplay = undefined;
        if (
            mode === "date" &&
            (typeof value === "string" || value instanceof Date)
        ) {
            valueToDisplay = formatDate(value, naText);
        } else if (mode === "timestamp") {
            if (typeof value === "number") {
                valueToDisplay = formatTimestamp(value, !!showTime);
            } else {
                console.error(
                    `timestamp must be a number.  value given: ${value}`
                );
                valueToDisplay = "invalid timestamp value";
            }
        } else if (mode === "money") {
            if (typeof value === "number") {
                valueToDisplay = currencyFormatter.format(value);
            } else {
                console.error(
                    `invalid money format.  Must be type of number.  value give: ${value}`
                );
                valueToDisplay = "invalid money value";
            }
        } else if (mode === "percentage") {
            if (typeof value === "number" || typeof value === "string") {
                valueToDisplay = `${value}%`;
            } else {
                console.error(
                    `invalid percentage format.  Must be type of number.  value give: ${value}`
                );
                valueToDisplay = "invalid percentage value";
            }
        } else if (mode === "url") {
            if (typeof value === "string") {
                valueToDisplay = <WrappedUrl url={value} />;
            } else {
                console.error(
                    `invalid url format.  Must be type of string.  value give: ${value}`
                );
                valueToDisplay = "invalid url value";
            }
        } else if (mode === "textWithLinks" && typeof value === "string") {
            valueToDisplay = (
                <TextWithLinks>{textWithLinks(value)}</TextWithLinks>
            );
        } else {
            valueToDisplay = value.toString();
        }

        if (!!startElement || !!endElement) {
            setDisplayValue(
                <AdornmentContainer>
                    {startElement}
                    <Text
                        className={textClasses}
                        variant="body1"
                        component="div"
                        preserveFormat={preserveTextFormat}
                        dataCy={textCy}
                    >
                        {valueToDisplay}
                        {!!customElement && ` ${customElement}`}
                    </Text>
                    {endElement}
                </AdornmentContainer>
            );
        } else {
            setDisplayValue(
                <div>
                    <Text
                        variant="body1"
                        component="div"
                        preserveFormat={preserveTextFormat}
                        dataCy={textCy}
                        //className={classnames({[classes.inline]: inline})}
                    >
                        {valueToDisplay}
                        {!!customElement && ` ${customElement}`}
                    </Text>
                </div>
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        value,
        mode,
        component,
        notAvailableText,
        showTime,
        startElement,
        endElement,
        preserveTextFormat,
        textCy,
        hidden,
        customElement,
    ]);

    if (hidden) return null;
    if (hideWhenEmpty && (value === undefined || value === null)) {
        return null;
    }

    return (
        <div>
            {showHeader && headerText !== "" ? (
                <Text gutterBottom variant="subtitle2">
                    {headerText}
                </Text>
            ) : null}

            {showHeader && headerText == "" ? (
                <Text gutterBottom variant="subtitle2">
                    &nbsp;
                </Text>
            ) : null}

            <Label
                label={label}
                inline={!!inline}
                fullWidth={!!fullWidth}
                {...passthrough}
                data-cy={labelCy}
            >
                <>{displayValue}</>
            </Label>
        </div>
    );
};
